import styles from './Auth.module.css';

import type { FC, ReactNode } from 'react';

import classNames from 'classnames';
import React, { useState } from 'react';
import { X } from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router';

import { useDocumentTitle } from 'src/hooks/useDocumentTitle';

import { submitUnless } from 'src/utils/submitUnless';
import { resolveSignInReturnTo } from 'src/utils/signInReturnTo';

import { useUserLogin, useResendUserOtp, useConfirmUserLogin } from 'src/queries';
// From the module itself, not the barrel: the screen's tests stand the barrel in, and an error class
// is a thing to tell apart, not a hook to replace.
import { SessionNotKeptError, SignInUnconfirmedError } from 'src/queries/useConfirmUserLogin';

import { Stack } from '../../components/themed/Stack';
import { Button } from '../../components/themed/Button';
import { Typography } from '../../components/themed/Typography';
import { ActionButton } from '../../components/themed/ActionButton';
import {
  checkLoginEmail,
  isLoginCodeReady,
  describeLoginError,
  normalizeLoginCode,
  loginEmailErrorCopy,
} from '../../utils/emailLogin';

interface AuthProps {
  modal?: boolean;
}

/**
 * The sign-in screen: e-mail and code, the one way in since api#402 (step 2 of the one-rail
 * track, Tim's decision of 2026-10-20). It works everywhere the storefront opens, Telegram's
 * built-in browser included — which the wallet sign-in it replaces never did. A wallet is not a
 * way in any more: the account links one in Profile and connects it at the moment of paying. A
 * brand-new visitor is sent to the role-neutral plan chooser to register, not signed in here.
 */
export const Auth: FC<AuthProps> = (props) => {
  const { modal } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string } | null;
  // The opener's `from`, or — when a gated screen rendered this form in its own place, as a
  // deep link from an email does — the address bar itself (app#234).
  const returnTo = resolveSignInReturnTo({
    from: state?.from,
    pathname: location.pathname,
    search: location.search,
  });
  useDocumentTitle('Sign in');

  const handleClose = () => {
    navigate(returnTo ?? '/', { replace: true });
  };

  // Where to land after signing in: a modal returns to its opener, a gated screen returns to
  // itself, the sign-in page goes home.
  const onSignedIn = () => {
    if (modal) {
      handleClose();
    } else {
      navigate(returnTo ?? '/', { replace: true });
    }
  };

  return (
    <div className={classNames('container', styles.container)}>
      {modal ? (
        <Stack alignItems="flex-end" p={4} mb={4} maxWidth="750px" width="100%">
          <ActionButton onClick={handleClose} label="Close">
            <X size={24} color="white" />
          </ActionButton>
        </Stack>
      ) : null}

      <Stack maxWidth="750px" width="100%" flex={1} justifyContent="center" alignItems="center">
        <Stack maxWidth="360px" width="100%" gap={6} ph={4}>
          <Typography as="h1" color="white" variant="title2" textAlign="center">
            Sign in
          </Typography>

          <EmailPanel onSignedIn={onSignedIn} />

          <Stack alignItems="center" gap={1}>
            <Typography color="lightWhite" variant="small" textAlign="center">
              New to GAC?
            </Typography>
            <button type="button" className={styles.linkButton} onClick={() => navigate('/plans')}>
              <Typography color="accent" variant="small">
                Choose your plan
              </Typography>
            </button>
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};

const EmailPanel: FC<{ onSignedIn: () => void }> = ({ onSignedIn }) => {
  const userLogin = useUserLogin();
  const confirmLogin = useConfirmUserLogin();
  const resendOtp = useResendUserOtp();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const handleSendCode = async () => {
    setError('');
    setNotice('');
    const check = checkLoginEmail(email);
    if (!check.ok) {
      setError(loginEmailErrorCopy(check.reason));
      return;
    }
    try {
      const { reference: ref } = await userLogin.mutateAsync({ email: check.email });
      setEmail(check.email);
      setReference(ref);
      setCode('');
      setStep('code');
    } catch (err) {
      setError(describeLoginError(err, 'Could not send the code. Please try again.'));
    }
  };

  const handleConfirm = async () => {
    setError('');
    if (!isLoginCodeReady(code)) {
      setError('Enter the code from your email.');
      return;
    }
    try {
      // The session is the api's cookie (api#454); the confirm has already asked `/user/auth/me` with
      // it, so when this resolves the browser kept the sign-in. A browser that did not (some
      // settings, some embedded webviews refuse a cookie from another host) is refused there and
      // says so below — leaving now would look like a sign-in that did not take; a question that got
      // no answer is told as that (reload — you may be signed in), not as a refused cookie.
      await confirmLogin.mutateAsync({ code: normalizeLoginCode(code), reference });
      onSignedIn();
    } catch (err) {
      if (err instanceof SessionNotKeptError || err instanceof SignInUnconfirmedError) {
        setError(err.message);
        return;
      }
      setError(describeLoginError(err, 'That code did not work. Please try again.'));
    }
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    try {
      const { reference: ref } = await resendOtp.mutateAsync(reference);
      setReference(ref);
      setNotice('A new code has been sent.');
    } catch (err) {
      setError(describeLoginError(err, 'Could not resend the code. Please try again.'));
    }
  };

  // What holds each step's button holds Enter too (`submitUnless`): an empty field, a request out.
  const cannotSendCode = userLogin.isPending || !email.trim();
  const cannotConfirm = confirmLogin.isPending || !code.trim();

  // Each step is a form, so Enter — Go on a phone keyboard — in its field does what its button
  // does (app#366). `noValidate`: the screen checks the address itself and says what is wrong in
  // its own words, as it did when the button was the only way.
  if (step === 'email') {
    return (
      <form noValidate onSubmit={submitUnless(cannotSendCode, handleSendCode)}>
        <Stack gap={4} width="100%">
          <Stack gap={1}>
            <Typography color="lightWhite" variant="small">
              Email
            </Typography>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-label="Email"
              className={styles.input}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Stack>

          {error ? <ErrorText>{error}</ErrorText> : null}

          <Button
            type="submit"
            title="Send code"
            variant="accent"
            loading={userLogin.isPending}
            disabled={cannotSendCode}
          />
        </Stack>
      </form>
    );
  }

  // The links below the button stay `type="button"`: a submit button among them would try the
  // typed code on a tap of Change email or Resend code.
  return (
    <form noValidate onSubmit={submitUnless(cannotConfirm, handleConfirm)}>
      <Stack gap={4} width="100%">
        {/* The address is inline (`span`): as a block it took a line of its own and left the full
            stop to start the next line on a phone. Centered like the heading above it. */}
        <Typography color="lightWhite" variant="small" textAlign="center">
          We emailed a code to{' '}
          <Typography as="span" color="white" fontWeight="600" variant="small">
            {email}
          </Typography>
          . Enter it below to sign in.
        </Typography>

        <Stack gap={1}>
          <Typography color="lightWhite" variant="small">
            Verification code
          </Typography>
          {/* The code is four capital letters and digits (api `generateCode(4)`), so the field
              must open the full keyboard — `numeric` gave iPhones a digit pad that cannot type
              the letters — and typed lowercase is sent in capitals (`normalizeLoginCode`). */}
          <input
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="one-time-code"
            aria-label="Verification code"
            className={styles.input}
            placeholder="A1B2"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </Stack>

        {error ? <ErrorText>{error}</ErrorText> : null}
        {notice ? <NoticeText>{notice}</NoticeText> : null}

        <Button
          type="submit"
          title="Sign in"
          variant="accent"
          loading={confirmLogin.isPending}
          disabled={cannotConfirm}
        />

        <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              setStep('email');
              setCode('');
              setError('');
              setNotice('');
            }}
          >
            <Typography color="lightWhite" variant="small">
              Change email
            </Typography>
          </button>
          <button
            type="button"
            className={styles.linkButton}
            onClick={handleResend}
            disabled={resendOtp.isPending}
          >
            <Typography color="lightWhite" variant="small">
              Resend code
            </Typography>
          </button>
        </Stack>
      </Stack>
    </form>
  );
};

const ErrorText = ({ children }: { children: ReactNode }) => (
  <div className={styles.error}>
    <Typography color="danger" variant="small">
      {children}
    </Typography>
  </div>
);

const NoticeText = ({ children }: { children: ReactNode }) => (
  <div className={styles.success}>
    <Typography color="white" variant="small">
      {children}
    </Typography>
  </div>
);
